import type { ReactNode } from "react";
import { useCallback, useMemo, useRef, useState } from "react";
import { styled } from "styled-components";

import {
  ScrollGroup,
  useScrollGroupContext,
  useScrollGroupTo,
} from "../ScrollGroup";
import type { StickyGridContextType } from "./context";
import { StickyGridContext } from "./context";

export function StickyGrid({
  children,
  templateColumns,
  gap,
}: {
  children: ReactNode;
  templateColumns: string;
  gap?: string;
}) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [isAtMinX, setIsAtMinX] = useState(true);
  const [isAtMaxX, setIsAtMaxX] = useState(true);

  const evaluateXEndpoints = useCallback(
    (element: HTMLElement) => {
      if (element.scrollLeft === 0) {
        if (!isAtMinX) {
          setIsAtMinX(true);
        }
      } else if (isAtMinX) {
        setIsAtMinX(false);
      }
      if (element.scrollLeft + 1 >= element.scrollWidth - element.clientWidth) {
        if (!isAtMaxX) {
          setIsAtMaxX(true);
        }
      } else if (isAtMaxX) {
        setIsAtMaxX(false);
      }
    },
    [isAtMaxX, isAtMinX],
  );

  const value = useMemo(
    () => ({
      templateColumns,
      gap,
      isAtMaxX,
      isAtMinX,
      setIsAtMaxX,
      setIsAtMinX,
    }),
    [isAtMaxX, isAtMinX, templateColumns, gap],
  );

  return (
    <ScrollGroup
      onScrollItem={evaluateXEndpoints}
      onAddItem={evaluateXEndpoints}
      onResizeItem={evaluateXEndpoints}
    >
      <StickyGridProvider value={value}>
        <StyledStickyGrid ref={wrapperRef}>{children}</StyledStickyGrid>
      </StickyGridProvider>
    </ScrollGroup>
  );
}

function StickyGridProvider({
  value,
  children,
}: {
  value: Omit<StickyGridContextType, "scrollTo" | "scrollGroupItemsRef">;
  children: ReactNode;
}) {
  const scrollTo = useScrollGroupTo();
  const { scrollGroupItemsRef } = useScrollGroupContext();
  return (
    <StickyGridContext.Provider
      value={useMemo(
        () => ({ ...value, scrollGroupItemsRef, scrollTo }),
        [scrollGroupItemsRef, scrollTo, value],
      )}
    >
      {children}
    </StickyGridContext.Provider>
  );
}

const StyledStickyGrid = styled.div`
  position: relative;
`;
